import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, MapPin, Phone, User, Truck, CheckCircle, AlertCircle } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { TeamLeadService } from '../../services/teamLeadService';

const CourierBooking = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
  const [selectedLead, setSelectedLead] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [courierType, setCourierType] = useState('standard');
  const [deliveryType, setDeliveryType] = useState('standard');
  const [coordinates, setCoordinates] = useState('');
  const [isBooking, setIsBooking] = useState(false);

  const { data: bookedOrdersData } = useQuery({
    queryKey: ['teamLeadBookedOrders', user?.id],
    queryFn: () => TeamLeadService.getBookedOrders(user?.id),
    enabled: !!user?.id,
  });

  const bookedOrders = bookedOrdersData?.data || [];

  const handleBookCourier = async () => {
    if (!selectedLead) {
      alert('Please select an order');
      return;
    }

    if (deliveryType === 'same_day' && !coordinates.trim()) {
      alert('Please enter location coordinates for same day delivery');
      return;
    }

    setIsBooking(true);
    try {
      // In a real implementation, this would call a courier booking service
      alert('Courier booking feature - This will integrate with courier service');
      setShowBookingModal(false);
      setSelectedLead(null);
      setCoordinates('');
      setDeliveryType('standard');
    } catch (error) {
      alert('Error booking courier: ' + error.message);
    } finally {
      setIsBooking(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate('/teamlead/more')}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft size={24} className="text-gray-700" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Courier Booking</h1>
          <p className="text-gray-600 mt-1">Book courier services for orders</p>
        </div>
      </div>

      {/* Booked Orders List */}
      {bookedOrders.length === 0 ? (
        <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-200 text-center">
          <Package size={48} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-500">No booked orders available for courier booking</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {bookedOrders.map((order) => (
            <div key={order.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-gray-900">Order #{order.id?.slice(0, 8)}</h3>
                  <p className="text-sm text-gray-600">{order.customer_name || 'Unknown'}</p>
                </div>
                {order.courier_name && (
                  <span className="px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-700">
                    Booked
                  </span>
                )}
              </div>

              <div className="space-y-2 text-sm mb-4">
                <div className="flex items-center space-x-2">
                  <Phone size={16} className="text-gray-400" />
                  <span className="text-gray-600">{order.contact_number || 'N/A'}</span>
                </div>
                <div className="flex items-start space-x-2">
                  <MapPin size={16} className="text-gray-400 mt-0.5" />
                  <span className="text-gray-600">{order.delivery_address || order.address || 'N/A'}</span>
                </div>
                {order.amount && (
                  <div className="flex items-center space-x-2">
                    <span className="text-gray-600">Amount:</span>
                    <span className="font-medium text-gray-900">
                      ₹{Number(order.amount).toLocaleString('en-IN')}
                    </span>
                  </div>
                )}
              </div>

              {!order.courier_name && (
                <button
                  onClick={() => {
                    setSelectedLead(order);
                    setShowBookingModal(true);
                  }}
                  className="w-full px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600"
                >
                  Book Courier
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Booking Modal */}
      {showBookingModal && selectedLead && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Book Courier</h2>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-2">Order</p>
                <p className="font-semibold text-gray-900">#{selectedLead.id?.slice(0, 8)}</p>
                <p className="text-sm text-gray-600">{selectedLead.customer_name || 'Unknown'}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Delivery Type</label>
                <select
                  value={deliveryType}
                  onChange={(e) => setDeliveryType(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="standard">Standard Delivery</option>
                  <option value="same_day">Same Day Delivery</option>
                  <option value="express">Express Delivery</option>
                </select>
              </div>

              {deliveryType === 'same_day' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Location Coordinates</label>
                  <input
                    type="text"
                    value={coordinates}
                    onChange={(e) => setCoordinates(e.target.value)}
                    placeholder="Enter GPS coordinates (lat, lng)"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                  <p className="text-xs text-gray-500 mt-1">Format: latitude, longitude (e.g., 28.6139, 77.2090)</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Courier Service</label>
                <select
                  value={courierType}
                  onChange={(e) => setCourierType(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="standard">Standard Courier</option>
                  <option value="express">Express Courier</option>
                  <option value="same_day">Same Day Delivery</option>
                </select>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => {
                    setShowBookingModal(false);
                    setSelectedLead(null);
                    setCoordinates('');
                    setDeliveryType('standard');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBookCourier}
                  disabled={isBooking || (deliveryType === 'same_day' && !coordinates.trim())}
                  className="flex-1 px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 disabled:opacity-50"
                >
                  {isBooking ? 'Booking...' : 'Book Courier'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourierBooking;

